import { memo } from "react"
import { Box, Grid, Stack, styled, Tooltip } from "@mui/material"
import { useLocalization } from "@tm/localization"
import { FastCalculatorOeArticleItemState } from "@tm/models"
import { useOeArticleItemState } from "../../ContextProviderStates/OeArticleItemState"
import { IconButton } from "../../../../muiStyled/Inputs/button"
import { Icon } from "../../../../muiStyled/DataDisplay/Icons"

import { Image } from "../../../image"
import { ImageContainer } from "../../StyledComponents"
import { Typography } from "../../../../muiStyled/DataDisplay/typographie"
import { ArticleNumber } from "../../../articleNumber"
import OeTransactions from "../../Transactions/OeTransactions"
import { PriceInputField } from "./PriceInputField"

const ArticleWrapper = styled(Box)({
    marginBottom: 9,
})

const ArticleContainer = styled(Box)({
    margin: "8px 0 0 0",
    backgroundColor: "#fafafa",
    boxShadow: "0 1px 5px rgb(0 0 0 / 20%);",
    width: "100%",
    borderBottom: "3px #c3c3c3 solid",
})

const GridItem = styled(Grid, {
    shouldForwardProp: (prop) => prop !== "smallLine",
})<{ smallLine?: boolean }>(({ smallLine }) => ({
    position: "relative",
    alignSelf: "stretch",

    "::after": {
        content: '""',
        position: "absolute",
        right: 0,
        top: smallLine ? ".75em" : 0,
        height: smallLine ? "40%" : "107%",
        borderRight: "solid 1px #c8c6c6",
    },
}))

export const FastCalculatorOeArticleItem = memo(() => {
    const { translateText } = useLocalization()

    const article = useOeArticleItemState((x) => x.article)
    const thumbnailUrl = useOeArticleItemState((x) => x.thumbnailUrl)
    const oeArticleNo = useOeArticleItemState((x) => x.oeArticleNo)
    const showArticleImages = useOeArticleItemState((x) => x.options.showArticleImages)
    const handleRemoveOeArticle = useOeArticleItemState((x: FastCalculatorOeArticleItemState) => x.handleRemoveOeArticle)

    return (
        <ArticleWrapper>
            <ArticleContainer>
                <Grid container direction="column">
                    {/* Top Row: A aligned right */}
                    <Grid item>
                        <Box display="flex" justifyContent="flex-end" pr={1}>
                            <IconButton onClick={handleRemoveOeArticle} size="large">
                                <Tooltip title={translateText(1)}>
                                    <Icon name="delete" />
                                </Tooltip>
                            </IconButton>
                        </Box>
                    </Grid>

                    {/* Main Content Row as-is */}
                    <Grid item>
                        <Grid container direction="row" wrap="nowrap" alignItems="start">
                            {/* Image column */}
                            <GridItem smallLine item flexBasis="15%">
                                <Stack spacing={1} alignItems="center" position="relative" bottom="0.3em" flexDirection="row" gap="1em" ml=".5em">
                                    <ImageContainer>
                                        {showArticleImages && <Image type="article" src={thumbnailUrl} loading="lazy" height="36px" width="36px" />}
                                    </ImageContainer>
                                    <Typography>{article.manufacturerName}</Typography>
                                </Stack>
                            </GridItem>
                            <GridItem smallLine item flexBasis="15%">
                                {!!oeArticleNo && (
                                    <Box height="100%" display="flex" alignItems="center" ml=".5em">
                                        <ArticleNumber articleNumber={oeArticleNo} articleNumberType="oe" />
                                    </Box>
                                )}
                            </GridItem>
                            <GridItem item flexBasis="20%">
                                {!!article.description && (
                                    <Box height="100%" display="flex" alignItems="center" ml=".5em">
                                        <Typography variant="body1">{article.description}</Typography>
                                    </Box>
                                )}
                            </GridItem>

                            <Box ml="1em" mt="-1em" flexGrow="1">
                                {/* The input itself */}
                                <PriceInputField />
                            </Box>
                            <Box height="100%" marginY="auto" pr="6px">
                                <OeTransactions />
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </ArticleContainer>
        </ArticleWrapper>
    )
})
